import PropTypes from 'prop-types';
import React from 'react';
import {map} from 'lodash';
import { sendSocialEvent } from '../../utils/analytics';
import { FaIcon } from '../common/fa_icons';
import { parse as parseUrl, format as formatUrl } from 'url';
import { defineMessages, injectIntl } from 'react-intl';
import { decorateIntlMessages } from '../common/rosetta_formatted_message';
import { EDITION_ID_FR_FR } from '../../constants';

const EMAIL_CLASSNAME = 'envelope-o';

const i18nMessages = decorateIntlMessages(__filename, defineMessages({
  email_link_title: {
    id: 'email_link_title',
    description: 'Email link title attribute content (Sign up for newsletters)',
    defaultMessage: 'Sign up for newsletters'
  },
  facebook_link_title: {
    id: 'facebook_link_title',
    description: 'Facebook link title attribute content (Visit Refinery29 on Facebook)',
    defaultMessage: 'Visit Refinery29 on Facebook'
  },
  twitter_link_title: {
    id: 'twitter_link_title',
    description: 'Twitter link title attribute content (Visit Refinery29 on Twitter)',
    defaultMessage: 'Visit Refinery29 on Twitter'
  },
  youtube_link_title: {
    id: 'youtube_link_title',
    description: 'YouTube link title attribute content (Visit Refinery29 on YouTube)',
    defaultMessage: 'Visit Refinery29 on YouTube'
  },
  instagram_link_title: {
    id: 'instagram_link_title',
    description: 'Instagram link title attribute content (Visit Refinery29 on Instagram)',
    defaultMessage: 'Visit Refinery29 on Instagram'
  },
  pinterest_link_title: {
    id: 'pinterest_link_title',
    description: 'Pinterest link title attribute content (Visit Refinery29 on Pinterest)',
    defaultMessage: 'Visit Refinery29 on Pinterest'
  },
  tiktok_link_title: {
    id: 'tiktok_link_title',
    description: 'TikTok link title attribute content (Visit Refinery29 on TikTok)',
    defaultMessage: 'Visit Refinery29 on TikTok'
  }
}));

const getSocialButtonProps = (intl, {
  email_signup_page,
  facebook_page = 'refinery29',
  instagram_handle = 'refinery29',
  pinterest_link = 'https://www.pinterest.com/refinery29/',
  tiktok_handle = '@refinery29',
  twitter_handle = 'refinery29',
  youtube_channel = 'refinery29tv'
} = {}, editionId) => {

  if (!email_signup_page) {
    email_signup_page = 'https://jump.refinery29.com/join/24/signup-all-newsletters';
  }

  let links = [
    {
      href: linkWithNewParams(
        email_signup_page, { utm_source: 'footer', utm_medium: 'emailsignup' }
      ),
      title: intl.formatMessage(i18nMessages.email_link_title),
      className: EMAIL_CLASSNAME,
      onClick: () => sendSocialEvent('Email', 'Signup', 'Global Nav')
    },
    {
      href: `https://www.facebook.com/${facebook_page}`,
      title: intl.formatMessage(i18nMessages.facebook_link_title),
      className: 'facebook',
      onClick: () => sendSocialEvent('Facebook', 'Like', 'Global Nav')
    },
    {
      href: `https://www.youtube.com/${youtube_channel}/?sub_confirmation=1`,
      title: intl.formatMessage(i18nMessages.youtube_link_title),
      className: 'youtube-play',
      onClick: () => sendSocialEvent('Youtube', 'Follow', 'Global Nav')
    },
    {
      href: `https://www.instagram.com/${instagram_handle}`,
      title: intl.formatMessage(i18nMessages.instagram_link_title),
      className: 'instagram',
      onClick: () => sendSocialEvent('Instagram', 'Follow', 'Global Nav')
    },
    {
      href: `https://twitter.com/${twitter_handle}`,
      title: intl.formatMessage(i18nMessages.twitter_link_title),
      className: 'twitter',
      onClick: () => sendSocialEvent('Twitter', 'Follow', 'Global Nav')
    },
    {
      href: linkWithNewParams(
        pinterest_link, { auto_follow: 'true' }
      ),
      title: intl.formatMessage(i18nMessages.pinterest_link_title),
      className: 'pinterest-p',
      onClick: () => sendSocialEvent('Pinterest', 'Follow', 'Global Nav')
    },
    {
      href: `https://www.tiktok.com/${tiktok_handle}`,
      title: intl.formatMessage(i18nMessages.tiktok_link_title),
      className: 'tiktok',
      onClick: () => sendSocialEvent('TikTok', 'Follow', 'Global Nav')
    }
  ];

  if (editionId === EDITION_ID_FR_FR) {
    links = links.filter(link => link.className !== EMAIL_CLASSNAME);
  }

  return links;
};


export const SocialNav = ({ edition_data, intl }) => (
  <ul className="social-nav">
    {
      map(
        getSocialButtonProps(intl, edition_data.config, edition_data.id),
        ({ href, className, onClick, title }) => (
          <li key={href} onClick={onClick}>
            <a href={href} target="_blank" title={title}>
              <FaIcon iconName={className} />
            </a>
          </li>
        )
      )
    }
  </ul>
);

SocialNav.propTypes = {
  edition_data: PropTypes.shape({
    id: PropTypes.number,
    config: PropTypes.shape({
      email_signup_page: PropTypes.string,
      facebook_page: PropTypes.string,
      twitter_handle: PropTypes.string,
      instagram_handle: PropTypes.string,
      pinterest_link: PropTypes.string,
      tiktok_handle: PropTypes.string,
      youtube_channel: PropTypes.string
    })
  }).isRequired,
  intl: PropTypes.object.isRequired
};

export default injectIntl(SocialNav);

function linkWithNewParams(link, newParams) {
  const parsedLink = parseUrl(link, true);
  return formatUrl({
    ...parsedLink,
    query: {
      ...parsedLink.query,
      ...newParams
    },
    search: null // `query` is only respected if `search` is not present
  });
}
